import {createApi} from '@reduxjs/toolkit/query/react';

import {
  AISupport,
  AuditLog,
  Config,
  Migrations,
  ParentPay,
  Purchases,
  Reports,
  Subscriptions,
  SystemHealth,
  Users,
} from '~/modules/api';
import {WeeklyChallengeList} from '~/types';
import {baseQuery} from './baseQuery';

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery,
  endpoints: builder => ({
    getConfig: builder.query<Config.GetConfigResponseData, void>({
      query: () => ({url: '/system-admin/config', method: 'GET'}),
    }),
    updateConfig: builder.mutation<void, Config.UpdateConfigRequestData>({
      query: data => ({url: '/system-admin/config', method: 'POST', data}),
    }),
    getAiSupportingData: builder.query<
      AISupport.GetAiSupportingDataResponse,
      void
    >({
      query: () => ({url: '/ai-support/supporting-data', method: 'GET'}),
    }),
    getQuestionSampleSets: builder.query<
      AISupport.GetQuestionSampleSetsResponse,
      void
    >({
      query: () => ({url: `/ai-support/question-samples`, method: 'GET'}),
    }),
    getQuestionSampleSetDetail: builder.query<
      AISupport.GetQuestionSampleSetDetailResponse,
      {id: string}
    >({
      query: ({id}) => ({
        url: `/ai-support/question-samples/${id}`,
        method: 'GET',
      }),
    }),
    getAutoMatchQuestions: builder.query<
      AISupport.GetAutoMatchQuestionsResponse,
      AISupport.GetAutoMatchQuestionsParams
    >({
      query: ({page, pageSize, prodRunMatchType}) => ({
        url: `/ai-support/question-auto-matches`,
        method: 'GET',
        params: {page, pageSize, ...(prodRunMatchType && {prodRunMatchType})},
      }),
    }),
    queueAutoPopulate: builder.mutation<void, {datocmsQuestionId: string}>({
      query: ({datocmsQuestionId}) => ({
        url: `/ai-support/question-auto-matches/queue-auto-populate`,
        method: 'POST',
        data: {datocmsQuestionId},
      }),
    }),
    queueAutoPublish: builder.mutation<
      void,
      {
        datocmsQuestionId: string;
        datocmsQuestionVersion: string;
        matchId: string;
        dryRun: boolean;
      }
    >({
      query: ({
        datocmsQuestionId,
        datocmsQuestionVersion,
        matchId,
        dryRun,
      }) => ({
        url: `/ai-support/question-auto-matches/queue-auto-publish`,
        method: 'POST',
        data: {datocmsQuestionId, datocmsQuestionVersion, matchId, dryRun},
      }),
    }),
    getAiToolTraces: builder.query<
      AISupport.GetAiToolTracesResponse,
      AISupport.GetAiToolTracesParams
    >({
      query: ({page, pageSize, startDate, endDate, traceName}) => ({
        url: `/ai-support/ai-tool-traces`,
        method: 'GET',
        params: {
          page,
          pageSize,
          ...(startDate && {startDate}),
          ...(endDate && {endDate}),
          ...(traceName && {traceName}),
        },
      }),
    }),
    getAiToolTraceDetail: builder.query<
      AISupport.GetAiToolTraceDetailResponse,
      AISupport.GetAiToolTraceDetailParams
    >({
      query: ({traceId}) => ({
        url: `/ai-support/ai-tool-trace-detail`,
        method: 'GET',
        params: {traceId},
      }),
    }),
    getAiToolTraceNames: builder.query<
      AISupport.GetAiToolTraceNamesResponse,
      void
    >({
      query: () => ({
        url: `/ai-support/ai-tool-trace-names`,
        method: 'GET',
      }),
    }),
    getAskStatsV2: builder.query<
      Reports.GetAskStatsV2Response,
      Reports.GetAskStatsV2Params
    >({
      query: ({periodLength, startDate}) => ({
        url: `/reports/askStatsV2`,
        method: 'GET',
        params: {periodLength, startDate},
      }),
    }),
    getQuestionMatchesQuality: builder.query<
      Reports.GetQuestionMatchesQualityResponse,
      Reports.GetQuestionMatchesQualityParams
    >({
      query: ({startDate, endDate}) => ({
        url: `/reports/ask/questionMatchesQuality`,
        method: 'GET',
        params: {startDate, endDate},
      }),
    }),
    getSystemHealthHistory: builder.query<
      SystemHealth.GetSystemHealthHistoryResponse,
      SystemHealth.GetSystemHealthHistoryParams
    >({
      query: ({days}) => ({
        url: `/system-admin/systemHealth/history`,
        method: 'GET',
        params: {days},
      }),
    }),
    getAuditLog: builder.query<
      AuditLog.GetAuditLogResponse,
      AuditLog.GetAuditLogParams
    >({
      query: ({endpoints}) => ({
        url: `/system-admin/auditLog`,
        method: 'GET',
        params: {endpoints: endpoints.join(',')},
      }),
    }),
    getPremiumStats: builder.query<Subscriptions.PremiumDataResponse, void>({
      query: () => ({url: '/subscriptions/getPremiumStats', method: 'GET'}),
    }),
    getDiscountCodes: builder.query<
      Subscriptions.GetDiscountCodesResponse,
      void
    >({
      query: () => ({url: '/subscriptions/discountCodes', method: 'GET'}),
    }),
    createDiscountCode: builder.mutation<
      Subscriptions.PutDiscountCodeResponse,
      Subscriptions.CreateDiscountCodeRequest
    >({
      query: data => ({
        url: '/subscriptions/discountCodes/' + data.id,
        method: 'PUT',
        data,
      }),
    }),
    updateDiscountCode: builder.mutation<
      Subscriptions.PutDiscountCodeResponse,
      Subscriptions.UpdateDiscountCodeRequest
    >({
      query: data => ({
        url: '/subscriptions/discountCodes/' + data.id,
        method: 'PUT',
        data,
      }),
    }),
    getDiscountCodesTracking: builder.query<
      Subscriptions.GetDiscountCodesTrackingResponse,
      {fromDate: string; toDate: string}
    >({
      query: ({fromDate, toDate}) => ({
        url: `/subscriptions/discountCodesTracking`,
        method: 'GET',
        params: {from: fromDate, to: toDate},
      }),
    }),
    createAccessCodes: builder.mutation<
      Subscriptions.CreateAccessCodesResponse,
      Subscriptions.CreateAccessCodesRequest
    >({
      query: data => ({
        url: '/subscriptions/accessCodes',
        method: 'POST',
        data,
      }),
    }),
    submitPdfConsent: builder.mutation<
      ParentPay.SubmitPdfConsentResponse,
      ParentPay.SubmitPdfConsentRequest
    >({
      query: data => ({
        url: '/parentConfig/minorInviteConsent',
        method: 'POST',
        data,
      }),
    }),
    resetTeenInvite: builder.mutation<void, ParentPay.ResetTeenInviteRequest>({
      query: data => ({
        url: '/parentConfig/resetTeenClaim',
        method: 'POST',
        data,
      }),
    }),
    parentLookup: builder.mutation<
      ParentPay.ParentDetail,
      ParentPay.ParentLookupRequest
    >({
      query: data => ({
        url: '/parentConfig/parentLookup',
        method: 'POST',
        data,
      }),
    }),
    webPurchasesLookup: builder.query<
      Purchases.WebPurchasesLookupResponse,
      Purchases.WebPurchasesLookupRequest
    >({
      query: data => ({
        url: '/purchases/webPurchasesLookup',
        method: 'POST',
        data,
      }),
    }),
    getUserListData: builder.query<
      Users.UserListResponse,
      Users.UserListParams
    >({
      query: ({userData, type}) => ({
        url: '/users/userListLookup',
        method: 'POST',
        data: {userData, type},
      }),
    }),
    getWeeklyChallengeList: builder.query<WeeklyChallengeList, void>({
      query: () => ({
        url: '/challenges/getWeeklyChallengeList',
        method: 'GET',
      }),
    }),

    /** MIGRATIONS **/
    getMigrationRuns: builder.query<Migrations.GetMigrationRunsResponse, void>({
      query: () => ({
        url: '/system-admin/migration/getMigrationRuns',
        method: 'GET',
      }),
    }),
    createMigrationRun: builder.mutation<
      Migrations.CreateMigrationRunResponse,
      Migrations.CreateMigrationRunRequest
    >({
      query: data => ({
        url: '/system-admin/migration/createMigrationRun',
        method: 'POST',
        data,
      }),
    }),
    getMigrationRunDetail: builder.query<
      Migrations.GetMigrationRunDetailResponse,
      {runId: string}
    >({
      query: ({runId}) => ({
        url: '/system-admin/migration/getMigrationRunDetail',
        method: 'POST',
        data: {runId},
      }),
    }),
    startMigrationRun: builder.mutation<
      void,
      Migrations.StartMigrationRunRequest
    >({
      query: ({runId, action}) => ({
        url: '/system-admin/migration/startMigrationRun',
        method: 'POST',
        data: {runId, action},
      }),
    }),
    retryFailedMigration: builder.mutation<void, {runId: string}>({
      query: ({runId}) => ({
        url: '/system-admin/migration/retryFailedMigration',
        method: 'POST',
        data: {runId},
      }),
    }),

    /** EXTRACTS **/
    runDatoSourceReferencesExtract: builder.mutation<string, void>({
      query: () => ({
        url: '/extracts/datoSourceReferences',
        method: 'POST',
        responseHandler: (response: Response) => response.text(),
      }),
    }),
  }),
});

export const {
  useGetConfigQuery,
  useUpdateConfigMutation,
  useGetQuestionSampleSetsQuery,
  useGetQuestionSampleSetDetailQuery,
  useGetAutoMatchQuestionsQuery,
  useQueueAutoPopulateMutation,
  useQueueAutoPublishMutation,
  useGetAiToolTracesQuery,
  useGetAiToolTraceDetailQuery,
  useGetAiToolTraceNamesQuery,
  useGetAskStatsV2Query,
  useGetQuestionMatchesQualityQuery,
  useGetPremiumStatsQuery,
  useGetSystemHealthHistoryQuery,
  useGetAuditLogQuery,
  useGetDiscountCodesQuery,
  useCreateDiscountCodeMutation,
  useCreateAccessCodesMutation,
  useUpdateDiscountCodeMutation,
  useGetDiscountCodesTrackingQuery,
  useSubmitPdfConsentMutation,
  useResetTeenInviteMutation,
  useParentLookupMutation,
  useWebPurchasesLookupQuery,
  useGetUserListDataQuery,
  useLazyGetUserListDataQuery,
  useGetAiSupportingDataQuery,
  useGetWeeklyChallengeListQuery,
  useLazyGetWeeklyChallengeListQuery,
  // migrations
  useGetMigrationRunsQuery,
  useCreateMigrationRunMutation,
  useGetMigrationRunDetailQuery,
  useStartMigrationRunMutation,
  useRetryFailedMigrationMutation,
  // extracts
  useRunDatoSourceReferencesExtractMutation,
} = adminApi;
